import * as React from 'react';
import { hydrate } from 'react-dom';
import { Widget } from '../types/widget';
import { encodedJSONParse } from '../utils/htmlEncode';
import { wrapperClassName, provideContext } from '../wrappers/util';
import { Args, InitialArgs } from '../types/request-context';
import { ClientsideLogger, Logger } from '../logger/logger';

export interface InitArgsAndProps<Props> {
    props: Props,
    initArgs: InitialArgs
}

export const hydrateWidgets = <T extends {}>(widget: Widget<T>, logger?: Logger) => {
    if (logger === undefined) {
        logger = new ClientsideLogger(`DVWebNode:${widget.name}`);
    }
    logger.info(`Hydrating ${widget.name}`);

    const componentsElements = document.getElementsByClassName(wrapperClassName(widget.name));
    if (componentsElements.length === 0) {
        // This can happen if the browser does not support the `defer` attribute on script tags
        if (document.readyState === 'loading') {
            logger.warn(`Waiting for document to finish loading to hydrate ${widget.name}`);
            document.addEventListener('DOMContentLoaded', () => {
                hydrateWidgets(widget, logger);
            });
        } else {
            logger.fatal(`Could not hydrate ${widget.name} as it is not present in the DOM.`);
        }
    } else {
        for (let i = 0; i < componentsElements.length; i++) {
            const componentElement = componentsElements[i];
            const script: Element|null = componentElement.nextElementSibling;
            if (script === null) {
                logger.fatal(`${wrapperClassName(widget.name)} does not include initArgs and props`);
            } else {
                const { initArgs, props } = encodedJSONParse(script.innerHTML) as InitArgsAndProps<T>
                // Construct Component
                const args: Args = { logger, ...initArgs }
                const WrappedWidget = provideContext(widget.Component, args);
                // Render
                hydrate(<WrappedWidget {...props} />, componentElement);
                logger.info(`Hydrated ${widget.name}`, componentElement);
            }
        }
    }
};
